import { toByteArray } from 'base64-js';
import type { GridStackWidget } from 'gridstack';
import type { Path, PathMarker, PathMarkerType, PathPreviewMode, Waypoint } from '../Components/PathBuilder/pathBuilderTypes';
import { DEFAULT_PATH_PREVIEW_MODE, PATH_MARKER_TYPES, PATH_PREVIEW_MODES } from '../Components/PathBuilder/pathBuilderTypes';
import { slotNumberFromSlotName } from './constants';
import type { FieldSelection } from './fieldManifest';
import { createDefaultFieldSelection, normalizeFieldSelection } from './fieldSelection';
import { getPathWaypoints } from './path-builder/path-builder-helpers';
import { migratePath } from './path-builder/path-segment-speeds';
import {
  CURRENT_PROJECT_VERSION,
  parsePartialVersionString,
  parseProjectVersionString,
  projectVersionToString,
  resolveDeployFrameworkVersionString,
} from './projectVersions';

/** Injected at deploy time only; not stored in editor buffer. */
const BPS_PROJECT_ID_MARKER = '__BPS_PROJECT_ID__=';
const BPS_PROJECT_NAME_MARKER = '__BPS_PROJECT_NAME__=';
const BPS_PROJECT_LAST_MODIFIED_UTC_MS_MARKER = '__BPS_PROJECT_LAST_MODIFIED_UTC_MS__=';
const BPS_PROJECT_VERSION_MARKER = '__BPS_PROJECT_VERSION__=';
const BPS_FRAMEWORK_VERSION_MARKER = '__BPS_FRAMEWORK_VERSION__=';
const BPS_PROJECT_FOOTER_MARKER = '__BPS_PROJECT_FOOTER__';
const BPS_DASHBOARD_MARKER = '__BPS_DASHBOARD__';
const BPS_PATHS_MARKER = '__BPS_PATHS__';

export const BPS_PROJECT_ID_COMMENT_PREFIX = `# ${BPS_PROJECT_ID_MARKER}`;
export const BPS_PROJECT_NAME_COMMENT_PREFIX = `# ${BPS_PROJECT_NAME_MARKER}`;
export const BPS_PROJECT_LAST_MODIFIED_UTC_MS_COMMENT_PREFIX = `# ${BPS_PROJECT_LAST_MODIFIED_UTC_MS_MARKER}`;
export const BPS_PROJECT_VERSION_COMMENT_PREFIX = `# ${BPS_PROJECT_VERSION_MARKER}`;
export const BPS_FRAMEWORK_VERSION_COMMENT_PREFIX = `# ${BPS_FRAMEWORK_VERSION_MARKER}`;
export const BPS_PROJECT_FOOTER_COMMENT_PREFIX = `# ${BPS_PROJECT_FOOTER_MARKER}`;
export const BPS_DASHBOARD_COMMENT_PREFIX = `# ${BPS_DASHBOARD_MARKER}`;
export const BPS_PATHS_COMMENT_PREFIX = `# ${BPS_PATHS_MARKER}`;

/** base64url-encoded UTF-8 JSON (chunked); safe for DU comment storage. */
export const BPS_DASHBOARD_ENCODING_B64U = 'b64u';
/**
 * Versioned compact dashboard JSON (chunked); current deploy format.
 * This is schema-compacted readable JSON stored in Python comments, not binary compression.
 * JSON punctuation and non-ASCII string values are intentionally left raw to avoid base64 expansion
 * over BLE; the legacy `b64u` reader stays available if DU comment storage ever requires an
 * ASCII-only payload.
 */
export const BPS_DASHBOARD_ENCODING_COMPACT_JSON = 'cjson';
/** Minified JSON split across comment lines (Phase 1 optional read path). */
export const BPS_DASHBOARD_ENCODING_PLAIN_JSON = 'plain-json';

/**
 * Versioned compact path-builder JSON (chunked); current deploy format for path metadata.
 * Intentionally shares the `cjson` token with {@link BPS_DASHBOARD_ENCODING_COMPACT_JSON}; the two payloads
 * live under separate section delimiters (`__BPS_DASHBOARD__` vs `__BPS_PATHS__`) so the token never collides.
 */
export const BPS_PATHS_ENCODING_COMPACT_JSON = 'cjson';

/**
 * Max UTF-8 byte length of the serialized dashboard metadata payload.
 * This is a conservative 64 KiB client-side guard for deploy/storage safety; firmware ignores the comment but still receives the bytes.
 */
export const BPS_DASHBOARD_DEPLOY_MAX_BYTES = 65_536;

/**
 * Max UTF-8 byte length of the serialized path metadata payload.
 * When exceeded, path metadata is omitted from deploy (and the caller is told via {@link BpsDeployMetadataResult})
 * rather than failing the deploy.
 *
 * This is a per-section early guard. The whole footer is additionally bounded by {@link BPS_DEPLOY_FOOTER_MAX_BYTES},
 * which catches the case where dashboard + paths each pass their own cap but together overflow. Firmware ignores
 * the footer comment, so these caps exist only for deploy/storage safety, mirroring {@link BPS_DASHBOARD_DEPLOY_MAX_BYTES}.
 */
export const BPS_PATHS_DEPLOY_MAX_BYTES = 65_536;

/**
 * Max UTF-8 byte length of the entire assembled deploy footer (base metadata + dashboard + paths sections,
 * including chunk-comment overhead). This is the combined ceiling the per-section caps cannot enforce on their own.
 * When exceeded, path metadata is dropped first (best-effort, reported via {@link BpsDeployMetadataResult}); if the
 * base metadata + dashboard still overflow, the deploy fails, mirroring {@link serializeDashboardLayoutForDeploy}.
 */
export const BPS_DEPLOY_FOOTER_MAX_BYTES = 65_536;

/** Max UTF-8 payload bytes per `# <chunk>` dashboard comment line. */
const BPS_DASHBOARD_CHUNK_LINE_MAX_BYTES = 400;

const BPS_DASHBOARD_CHUNK_PAYLOAD_REGEX = /^[A-Za-z0-9_-]+$/;
const LEGACY_COMPACT_DASHBOARD_LAYOUT_VERSION = 1;
const COMPACT_DASHBOARD_LAYOUT_VERSION = 2;
const COMPACT_DASHBOARD_WIDGET_FIELDS = ['x', 'y', 'w', 'h', 'content'] as const;
const LEGACY_COMPACT_DASHBOARD_CONTENT_FIELDS = ['dataKey', 'props', 'options', 'sensorKeys', 'curve', 'style'] as const;
const COMPACT_DASHBOARD_CONTENT_FIELDS = ['dataKey', 'options', 'sensorKeys', 'curve', 'style'] as const;
type CompactDashboardContentField = (typeof LEGACY_COMPACT_DASHBOARD_CONTENT_FIELDS)[number];

type CompactDashboardContentValue = string | string[] | Record<string, unknown> | null;
type CompactDashboardContent = CompactDashboardContentValue[];
type CompactDashboardWidgetValue = string | number | null | CompactDashboardContent;
type CompactDashboardWidget = CompactDashboardWidgetValue[];

interface CompactDashboardLayoutPayload {
  v: typeof COMPACT_DASHBOARD_LAYOUT_VERSION;
  w: CompactDashboardWidget[];
}

const COMPACT_PATHS_VERSION = 1;

const PATH_MARKER_TYPE_TO_INT: Record<PathMarkerType, number> = {
  [PATH_MARKER_TYPES.SEGMENT_PERCENT]: 0,
  [PATH_MARKER_TYPES.DISTANCE_FROM_START]: 1,
  [PATH_MARKER_TYPES.DISTANCE_FROM_END]: 2,
};

const INT_TO_PATH_MARKER_TYPE: Record<number, PathMarkerType> = {
  0: PATH_MARKER_TYPES.SEGMENT_PERCENT,
  1: PATH_MARKER_TYPES.DISTANCE_FROM_START,
  2: PATH_MARKER_TYPES.DISTANCE_FROM_END,
};

/** Positional compact waypoint: `[x, y, rad, rot]`. */
type CompactWaypoint = [number, number, number, number];
/** Positional compact marker action: `[selection, argValues]` or `[selection, argValues, rawExpression]`. */
type CompactMarkerAction = [string, Record<string, string>] | [string, Record<string, string>, string];
/** Positional compact marker: `[name, segmentIndex, typeInt, value]` with optional trailing action. */
type CompactMarker = [string, number, number, number] | [string, number, number, number, CompactMarkerAction];
/** Positional compact path: `[name, isVisible, wp, spd]` with optional trailing markers. */
type CompactPath = [string, number, CompactWaypoint[], number[]] | [string, number, CompactWaypoint[], number[], CompactMarker[]];

interface CompactPathsPayload {
  v: typeof COMPACT_PATHS_VERSION;
  /** Index into `p` of the selected path; omitted when none is selected. */
  sel?: number;
  /** Field selection: `[family, variant, accessoriesEnabled ? 1 : 0]`. */
  fld?: [FieldSelection['family'], FieldSelection['variant'], number];
  /** Path preview mode: `0` swerve, `1` skid. */
  pm?: number;
  /** Path-builder field rotation (degrees); omitted when `0`. */
  rot?: number;
  p: CompactPath[];
}

/** Path-builder settings serialized alongside paths in the deploy footer. */
export interface BpsPathsDeploySettings {
  selectedPathId?: string;
  selectedField?: FieldSelection;
  pathPreviewMode?: PathPreviewMode;
  pathBuilderFieldRotation?: number;
}

interface ParsedPathsDeployMetadata extends BpsPathsDeploySettings {
  paths: Path[];
}

/** Compact deploy footer line: `# <value>` with no field marker (order is positional after the delimiter). */
export function formatBpsDeployMetadataValueCommentLine(value: string | number): string {
  return `# ${value}`;
}

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const BPS_COMPACT_DEPLOY_FOOTER_METADATA_LINE_COUNT = 5;

/**
 * TODO(pre-release): Remove legacy BPS deploy-metadata read-back support in this section.
 * Slot program metadata has not shipped to production; the parsers below exist only so QA/dev
 * can recover on-device Python from internal builds (leading marker headers, variable-length
 * compact footers, marker-based trailing footers). Before release, keep fixed-order 5-line
 * delimited EOF footers ({@link parseFixedOrderCompactBpsDeployMetadataLines}) only.
 */
/** Legacy leading deploy headers appended at most three metadata comment lines. */
const MAX_BPS_LEGACY_DEPLOY_FOOTER_LINES = 3;

export class BpsDashboardDeployMetadataError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'BpsDashboardDeployMetadataError';
  }
}

export interface BpsProjectDeployMetadata {
  projectId?: string;
  projectName?: string;
  projectLastModifiedUtcMs?: number;
  projectVersion?: string;
  /** Parsed from deploy footers; ignored by {@link appendBpsProjectMetadataToDeployedPython}. */
  frameworkVersion?: string;
  /** Dashboard layout embedded after the project footer on deploy; omitted when empty on write. */
  dashboardLayout?: GridStackWidget[];
  /** Path-builder paths embedded after the dashboard section on deploy; omitted when empty on write. */
  paths?: Path[];
  /** Selected path id (path-builder), round-tripped with {@link paths}. */
  selectedPathId?: string;
  /** Field selection (path-builder), round-tripped with {@link paths}. */
  selectedField?: FieldSelection;
  /** Path preview mode (path-builder), round-tripped with {@link paths}. */
  pathPreviewMode?: PathPreviewMode;
  /** Path-builder field rotation in degrees, round-tripped with {@link paths}. */
  pathBuilderFieldRotation?: number;
}

function normalizeProjectName(projectName: string | null | undefined): string | undefined {
  if (typeof projectName !== 'string') return undefined;
  const normalized = projectName.replace(/\r?\n/g, ' ').trim();
  return normalized || undefined;
}

function normalizeProjectLastModifiedUtcMs(projectLastModifiedUtcMs: number | string | null | undefined): number | undefined {
  const normalized = typeof projectLastModifiedUtcMs === 'string' ? Number(projectLastModifiedUtcMs.trim()) : projectLastModifiedUtcMs;
  if (typeof normalized !== 'number' || !Number.isInteger(normalized) || normalized <= 0) {
    return undefined;
  }
  return normalized;
}

function normalizeVersionString(value: string | null | undefined): string | undefined {
  if (typeof value !== 'string') return undefined;
  const parsed = parseProjectVersionString(value) ?? parsePartialVersionString(value);
  return parsed ? projectVersionToString(parsed) : undefined;
}

function normalizeStoredFrameworkVersion(value: string | null | undefined): string {
  return resolveDeployFrameworkVersionString(value);
}

export function parseProjectLastModifiedToUtcMs(lastModified: string | null | undefined): number | undefined {
  if (typeof lastModified !== 'string') return undefined;
  const parsed = Date.parse(lastModified);
  return Number.isFinite(parsed) ? parsed : undefined;
}

export function projectLastModifiedUtcMsToIsoString(projectLastModifiedUtcMs: number | undefined): string | undefined {
  if (typeof projectLastModifiedUtcMs !== 'number' || !Number.isFinite(projectLastModifiedUtcMs)) {
    return undefined;
  }
  return new Date(projectLastModifiedUtcMs).toISOString();
}

function isBpsDashboardDelimiterLine(line: string): boolean {
  return line.trim() === BPS_DASHBOARD_COMMENT_PREFIX;
}

function isBpsPathsDelimiterLine(line: string): boolean {
  return line.trim() === BPS_PATHS_COMMENT_PREFIX;
}

function formatBpsDashboardEncodingCommentLine(encoding: string): string {
  return `# ${encoding}`;
}

function formatBpsDashboardChunkCommentLine(chunk: string): string {
  return `# ${chunk}`;
}

function readBpsCommentPayloadLine(line: string): string {
  const trimmedStart = line.trimStart();
  if (!trimmedStart.startsWith('#')) return '';

  const payload = trimmedStart.slice(1);
  return payload.startsWith(' ') ? payload.slice(1) : payload.trimStart();
}

function base64UrlToBytes(payload: string): Uint8Array {
  let base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
  const padding = base64.length % 4;
  if (padding > 0) {
    base64 += '='.repeat(4 - padding);
  }
  return toByteArray(base64);
}

function encodeUtf8(value: string): Uint8Array {
  if (typeof TextEncoder !== 'undefined') {
    return new TextEncoder().encode(value);
  }
  return Uint8Array.from(value, (char) => char.charCodeAt(0) & 0xff);
}

function decodeUtf8(bytes: Uint8Array): string {
  if (typeof TextDecoder !== 'undefined') {
    return new TextDecoder().decode(bytes);
  }
  return String.fromCharCode(...bytes);
}

function getUtf8ByteLength(value: string): number {
  return encodeUtf8(value).length;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function hasOwnProperty(value: Record<string, unknown>, property: string): boolean {
  return Object.prototype.hasOwnProperty.call(value, property);
}

function normalizeDashboardWidgetForDeploy(widget: unknown): GridStackWidget | null {
  if (!isRecord(widget)) return null;
  const id = typeof widget.id === 'string' ? widget.id.trim() : '';
  if (!id) return null;

  const normalized: GridStackWidget = { id };
  if (typeof widget.x === 'number' && Number.isFinite(widget.x)) normalized.x = widget.x;
  if (typeof widget.y === 'number' && Number.isFinite(widget.y)) normalized.y = widget.y;
  if (typeof widget.w === 'number' && Number.isFinite(widget.w)) normalized.w = widget.w;
  if (typeof widget.h === 'number' && Number.isFinite(widget.h)) normalized.h = widget.h;
  if (typeof widget.content === 'string') normalized.content = widget.content;

  return normalized;
}

export function normalizeDashboardLayoutForDeploy(layout: GridStackWidget[] | null | undefined): GridStackWidget[] {
  if (!Array.isArray(layout)) return [];
  return layout.map(normalizeDashboardWidgetForDeploy).filter((widget): widget is GridStackWidget => widget !== null);
}

function compactDashboardWidgetContentForDeploy(content: string): string | CompactDashboardContent {
  try {
    const parsed: unknown = JSON.parse(content);
    if (!isRecord(parsed) || typeof parsed.name !== 'string' || !parsed.name) {
      return content;
    }

    const values: CompactDashboardContent = [parsed.name];
    const optionalValues: CompactDashboardContentValue[] = COMPACT_DASHBOARD_CONTENT_FIELDS.map((field) => {
      const value = parsed[field];
      if (field === 'dataKey') {
        return hasOwnProperty(parsed, field) ? (typeof value === 'string' ? value : String(value ?? '')) : null;
      }
      if (field === 'options') {
        return hasOwnProperty(parsed, field) && isRecord(value) ? value : null;
      }
      if (field === 'sensorKeys') {
        const sensorKeys = Array.isArray(value) ? value.filter((key): key is string => typeof key === 'string' && key.length > 0) : [];
        return hasOwnProperty(parsed, field) ? sensorKeys : null;
      }
      if (field === 'curve') {
        return typeof value === 'string' ? value : null;
      }
      return typeof value === 'string' ? value : null;
    });

    let lastValueIndex = optionalValues.length - 1;
    while (lastValueIndex >= 0 && optionalValues[lastValueIndex] === null) {
      lastValueIndex -= 1;
    }

    for (let index = 0; index <= lastValueIndex; index += 1) {
      values.push(optionalValues[index]);
    }

    return values;
  } catch {
    return content;
  }
}

function expandCompactDashboardWidgetContentForDeploy(
  value: unknown,
  contentFields: readonly CompactDashboardContentField[] = COMPACT_DASHBOARD_CONTENT_FIELDS
): string | undefined {
  if (typeof value === 'string') return value;
  if (!Array.isArray(value)) return undefined;

  const name = typeof value[0] === 'string' ? value[0] : '';
  if (!name) return undefined;

  let dataKey: string | undefined;
  let options: Record<string, unknown> | undefined;
  let sensorKeys: string[] | undefined;
  let hasSensorKeys = false;
  let curve: string | undefined;
  let style: string | undefined;

  contentFields.forEach((field, index) => {
    const fieldValue = value[index + 1];
    if (field === 'dataKey') {
      if (typeof fieldValue === 'string') {
        dataKey = fieldValue;
      }
      return;
    }
    if (field === 'options') {
      if (isRecord(fieldValue)) {
        options = fieldValue;
      }
      return;
    }
    if (field === 'sensorKeys') {
      if (Array.isArray(fieldValue)) {
        sensorKeys = fieldValue.filter((key): key is string => typeof key === 'string' && key.length > 0);
        hasSensorKeys = true;
      }
      return;
    }
    if (typeof fieldValue === 'string') {
      if (field === 'curve') {
        curve = fieldValue;
      } else {
        style = fieldValue;
      }
    }
  });

  const content: Record<string, unknown> = {
    name,
    ...(curve ? { curve } : {}),
    ...(style ? { style } : {}),
    ...(dataKey !== undefined ? { dataKey } : {}),
    ...(hasSensorKeys ? { sensorKeys: sensorKeys ?? [] } : {}),
    ...(options ? { options } : {}),
  };

  return JSON.stringify(content);
}

function compactDashboardWidgetForDeploy(widget: GridStackWidget): CompactDashboardWidget {
  const values: CompactDashboardWidget = [widget.id ?? ''];
  const optionalValues = COMPACT_DASHBOARD_WIDGET_FIELDS.map((field) => {
    const value = widget[field];
    if (field === 'content') {
      return typeof value === 'string' ? compactDashboardWidgetContentForDeploy(value) : null;
    }
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
  });

  let lastValueIndex = optionalValues.length - 1;
  while (lastValueIndex >= 0 && optionalValues[lastValueIndex] === null) {
    lastValueIndex -= 1;
  }

  for (let index = 0; index <= lastValueIndex; index += 1) {
    const value = optionalValues[index];
    values.push(value);
  }

  return values;
}

function compactDashboardLayoutForDeploy(layout: GridStackWidget[]): CompactDashboardLayoutPayload {
  return {
    v: COMPACT_DASHBOARD_LAYOUT_VERSION,
    w: layout.map(compactDashboardWidgetForDeploy),
  };
}

function expandCompactDashboardWidgetForDeploy(value: unknown, contentFields?: readonly CompactDashboardContentField[]): GridStackWidget | null {
  if (!Array.isArray(value)) return null;

  const id = typeof value[0] === 'string' ? value[0] : '';
  const widget: Record<string, unknown> = { id };

  COMPACT_DASHBOARD_WIDGET_FIELDS.forEach((field, index) => {
    const fieldValue = value[index + 1];
    if (field === 'content') {
      const content = expandCompactDashboardWidgetContentForDeploy(fieldValue, contentFields);
      if (content !== undefined) {
        widget[field] = content;
      }
      return;
    }

    if (typeof fieldValue === 'number' && Number.isFinite(fieldValue)) {
      widget[field] = fieldValue;
    }
  });

  return normalizeDashboardWidgetForDeploy(widget);
}

function parseDashboardLayoutJson(json: string): GridStackWidget[] {
  try {
    const parsed: unknown = JSON.parse(json);
    if (!Array.isArray(parsed)) return [];
    return normalizeDashboardLayoutForDeploy(parsed as GridStackWidget[]);
  } catch {
    return [];
  }
}

function parseCompactDashboardLayoutJson(json: string): GridStackWidget[] {
  try {
    const parsed: unknown = JSON.parse(json);
    if (
      !isRecord(parsed) ||
      (parsed.v !== COMPACT_DASHBOARD_LAYOUT_VERSION && parsed.v !== LEGACY_COMPACT_DASHBOARD_LAYOUT_VERSION) ||
      !Array.isArray(parsed.w)
    ) {
      return [];
    }
    const contentFields =
      parsed.v === LEGACY_COMPACT_DASHBOARD_LAYOUT_VERSION ? LEGACY_COMPACT_DASHBOARD_CONTENT_FIELDS : COMPACT_DASHBOARD_CONTENT_FIELDS;
    return parsed.w
      .map((widget) => expandCompactDashboardWidgetForDeploy(widget, contentFields))
      .filter((widget): widget is GridStackWidget => widget !== null);
  } catch {
    return [];
  }
}

function chunkDashboardDeployPayload(payload: string): string[] {
  const chunks: string[] = [];
  let chunk = '';
  let chunkBytes = 0;

  for (const char of payload) {
    const charBytes = getUtf8ByteLength(char);
    if (chunk && chunkBytes + charBytes > BPS_DASHBOARD_CHUNK_LINE_MAX_BYTES) {
      chunks.push(chunk);
      chunk = '';
      chunkBytes = 0;
    }
    chunk += char;
    chunkBytes += charBytes;
  }

  if (chunk) {
    chunks.push(chunk);
  }

  return chunks;
}

function splitDeployFooterLines(footerLines: string[]): {
  projectSectionLines: string[];
  dashboardSectionLines: string[] | null;
  pathsSectionLines: string[] | null;
} {
  const dashboardDelimiterIndex = footerLines.findIndex(isBpsDashboardDelimiterLine);
  const pathsDelimiterIndex = footerLines.findIndex(isBpsPathsDelimiterLine);

  const sectionStartIndexes = [dashboardDelimiterIndex, pathsDelimiterIndex].filter((index) => index >= 0);
  const projectSectionEnd = sectionStartIndexes.length > 0 ? Math.min(...sectionStartIndexes) : footerLines.length;
  const projectSectionLines = footerLines.slice(0, projectSectionEnd);

  let dashboardSectionLines: string[] | null = null;
  if (dashboardDelimiterIndex >= 0) {
    const dashboardEnd = pathsDelimiterIndex > dashboardDelimiterIndex ? pathsDelimiterIndex : footerLines.length;
    dashboardSectionLines = footerLines.slice(dashboardDelimiterIndex, dashboardEnd);
  }

  let pathsSectionLines: string[] | null = null;
  if (pathsDelimiterIndex >= 0) {
    const pathsEnd = dashboardDelimiterIndex > pathsDelimiterIndex ? dashboardDelimiterIndex : footerLines.length;
    pathsSectionLines = footerLines.slice(pathsDelimiterIndex, pathsEnd);
  }

  return { projectSectionLines, dashboardSectionLines, pathsSectionLines };
}

function parseDashboardLayoutFromDeployLines(dashboardSectionLines: string[]): GridStackWidget[] {
  if (dashboardSectionLines.length < 2 || !isBpsDashboardDelimiterLine(dashboardSectionLines[0])) {
    return [];
  }

  const encoding = readBpsCommentPayloadLine(dashboardSectionLines[1]).trim();
  const payload = dashboardSectionLines.slice(2).map(readBpsCommentPayloadLine).join('');

  if (!payload) return [];

  if (encoding === BPS_DASHBOARD_ENCODING_B64U) {
    if (!BPS_DASHBOARD_CHUNK_PAYLOAD_REGEX.test(payload)) return [];
    try {
      const json = decodeUtf8(base64UrlToBytes(payload));
      return parseDashboardLayoutJson(json);
    } catch {
      return [];
    }
  }

  if (encoding === BPS_DASHBOARD_ENCODING_PLAIN_JSON) {
    return parseDashboardLayoutJson(payload);
  }

  if (encoding === BPS_DASHBOARD_ENCODING_COMPACT_JSON) {
    return parseCompactDashboardLayoutJson(payload);
  }

  return [];
}

/**
 * Serializes a dashboard layout into deploy comment lines (delimiter, encoding, chunked payload).
 * Uses compact versioned JSON ({@link BPS_DASHBOARD_ENCODING_COMPACT_JSON}).
 */
export function serializeDashboardLayoutForDeploy(layout: GridStackWidget[] | null | undefined): string[] {
  const normalizedLayout = normalizeDashboardLayoutForDeploy(layout);
  if (normalizedLayout.length === 0) return [];

  const json = JSON.stringify(compactDashboardLayoutForDeploy(normalizedLayout));
  const jsonByteLength = getUtf8ByteLength(json);
  if (jsonByteLength > BPS_DASHBOARD_DEPLOY_MAX_BYTES) {
    throw new BpsDashboardDeployMetadataError(`Dashboard deploy metadata exceeds ${BPS_DASHBOARD_DEPLOY_MAX_BYTES} bytes (${jsonByteLength} bytes)`);
  }

  const chunkLines = chunkDashboardDeployPayload(json).map(formatBpsDashboardChunkCommentLine);

  return [BPS_DASHBOARD_COMMENT_PREFIX, formatBpsDashboardEncodingCommentLine(BPS_DASHBOARD_ENCODING_COMPACT_JSON), ...chunkLines];
}

/**
 * Parses dashboard layout from deploy footer comments in Python read back from a device slot.
 */
export function parseDashboardLayoutFromPythonSource(source: string): GridStackWidget[] {
  if (!source || typeof source !== 'string') return [];

  const lines = source.split(/\r?\n/);
  const trailingRange = findTrailingBpsDeployFooterLineRange(lines);
  if (!trailingRange) return [];

  const { dashboardSectionLines } = splitDeployFooterLines(lines.slice(trailingRange.start, trailingRange.end));
  if (!dashboardSectionLines) return [];

  return parseDashboardLayoutFromDeployLines(dashboardSectionLines);
}

/** Keeps only string-valued entries so hand-edited/legacy footers cannot inject non-string arg values. */
function toStringRecord(value: Record<string, unknown>): Record<string, string> {
  const result: Record<string, string> = {};
  for (const [key, entry] of Object.entries(value)) {
    if (typeof entry === 'string') {
      result[key] = entry;
    }
  }
  return result;
}

function compactWaypointForDeploy(waypoint: Waypoint): CompactWaypoint {
  return [waypoint.x, waypoint.y, waypoint.rad, waypoint.rot];
}

function compactMarkerForDeploy(marker: PathMarker): CompactMarker {
  const typeInt = PATH_MARKER_TYPE_TO_INT[marker.type] ?? 0;
  if (marker.action) {
    const argValues = isRecord(marker.action.argValues) ? toStringRecord(marker.action.argValues) : {};
    const action: CompactMarkerAction =
      marker.action.rawExpression !== undefined
        ? [marker.action.selection ?? '', argValues, marker.action.rawExpression]
        : [marker.action.selection ?? '', argValues];
    return [marker.name, marker.segmentIndex, typeInt, marker.value, action];
  }
  return [marker.name, marker.segmentIndex, typeInt, marker.value];
}

function compactPathForDeploy(path: Path): CompactPath {
  const wp = getPathWaypoints(path).map(compactWaypointForDeploy);
  const spd = Array.isArray(path.spd) ? path.spd : [];
  const isVisible = path.isVisible === false ? 0 : 1;
  if (path.mrk?.length) {
    return [path.name, isVisible, wp, spd, path.mrk.map(compactMarkerForDeploy)];
  }
  return [path.name, isVisible, wp, spd];
}

function normalizePathsForDeploy(paths: Path[] | null | undefined): Path[] {
  if (!Array.isArray(paths)) return [];
  return paths.filter(
    (path): path is Path => isRecord(path) && typeof path.name === 'string' && path.name.length > 0 && Array.isArray((path as Path).wp)
  );
}

function compactPathsPayloadForDeploy(paths: Path[], settings: BpsPathsDeploySettings): CompactPathsPayload {
  const payload: CompactPathsPayload = { v: COMPACT_PATHS_VERSION, p: paths.map(compactPathForDeploy) };

  const selectedIndex = settings.selectedPathId
    ? paths.findIndex((path) => path.id === settings.selectedPathId || path.name === settings.selectedPathId)
    : -1;
  if (selectedIndex >= 0) {
    payload.sel = selectedIndex;
  }

  if (settings.selectedField) {
    payload.fld = [settings.selectedField.family, settings.selectedField.variant, settings.selectedField.accessoriesEnabled ? 1 : 0];
  }

  if (settings.pathPreviewMode) {
    payload.pm = settings.pathPreviewMode === PATH_PREVIEW_MODES.SKID ? 1 : 0;
  }

  if (
    typeof settings.pathBuilderFieldRotation === 'number' &&
    Number.isFinite(settings.pathBuilderFieldRotation) &&
    settings.pathBuilderFieldRotation !== 0
  ) {
    payload.rot = settings.pathBuilderFieldRotation;
  }

  return payload;
}

/**
 * True when path-builder settings differ from their defaults and are therefore worth persisting even when the
 * project has no paths (a non-default field selection, preview mode, or field rotation). `selectedPathId` is
 * intentionally ignored: it is only meaningful when paths exist.
 */
function hasNonDefaultPathBuilderSettings(settings: BpsPathsDeploySettings): boolean {
  const { selectedField, pathPreviewMode, pathBuilderFieldRotation } = settings;

  if (typeof pathBuilderFieldRotation === 'number' && Number.isFinite(pathBuilderFieldRotation) && pathBuilderFieldRotation !== 0) {
    return true;
  }

  if (pathPreviewMode && pathPreviewMode !== DEFAULT_PATH_PREVIEW_MODE) {
    return true;
  }

  if (selectedField) {
    const normalized = normalizeFieldSelection(selectedField);
    const defaults = createDefaultFieldSelection();
    if (
      normalized.family !== defaults.family ||
      normalized.variant !== defaults.variant ||
      normalized.accessoriesEnabled !== defaults.accessoriesEnabled
    ) {
      return true;
    }
  }

  return false;
}

/**
 * Serializes path-builder paths and settings into deploy comment lines (delimiter, encoding, chunked payload).
 * Also emits a settings-only section (empty `p`) when the project has no paths but carries non-default field
 * selection / preview mode / field rotation, so that state round-trips through a slot instead of resetting.
 * Returns `omitted: true` (with no lines) when the payload exceeds {@link BPS_PATHS_DEPLOY_MAX_BYTES}, so the
 * deploy can proceed without path metadata rather than failing.
 */
export function serializePathsForDeploy(
  paths: Path[] | null | undefined,
  settings: BpsPathsDeploySettings = {}
): { lines: string[]; omitted: boolean } {
  const normalizedPaths = normalizePathsForDeploy(paths);
  if (normalizedPaths.length === 0 && !hasNonDefaultPathBuilderSettings(settings)) {
    return { lines: [], omitted: false };
  }

  const json = JSON.stringify(compactPathsPayloadForDeploy(normalizedPaths, settings));
  const jsonByteLength = getUtf8ByteLength(json);
  if (jsonByteLength > BPS_PATHS_DEPLOY_MAX_BYTES) {
    return { lines: [], omitted: true };
  }

  const chunkLines = chunkDashboardDeployPayload(json).map(formatBpsDashboardChunkCommentLine);
  return {
    lines: [BPS_PATHS_COMMENT_PREFIX, formatBpsDashboardEncodingCommentLine(BPS_PATHS_ENCODING_COMPACT_JSON), ...chunkLines],
    omitted: false,
  };
}

function expandCompactWaypointForDeploy(value: unknown): Waypoint | null {
  if (!Array.isArray(value)) return null;
  const [x, y, rad, rot] = value;
  if (typeof x !== 'number' || !Number.isFinite(x) || typeof y !== 'number' || !Number.isFinite(y)) {
    return null;
  }
  return {
    id: '',
    x,
    y,
    rad: typeof rad === 'number' && Number.isFinite(rad) ? rad : 0,
    rot: typeof rot === 'number' && Number.isFinite(rot) ? rot : 0,
  };
}

function expandCompactMarkerForDeploy(value: unknown, pathName: string, index: number): PathMarker | null {
  if (!Array.isArray(value)) return null;
  const name = typeof value[0] === 'string' ? value[0] : '';
  if (!name) return null;

  const segmentIndex = typeof value[1] === 'number' && Number.isFinite(value[1]) ? value[1] : 0;
  const typeInt = typeof value[2] === 'number' ? value[2] : 0;
  const type = INT_TO_PATH_MARKER_TYPE[typeInt] ?? PATH_MARKER_TYPES.SEGMENT_PERCENT;
  const markerValue = typeof value[3] === 'number' && Number.isFinite(value[3]) ? value[3] : 0;

  const marker: PathMarker = {
    id: `${pathName}_marker_${index + 1}`,
    name,
    segmentIndex,
    type,
    value: markerValue,
  };

  const actionValue = value[4];
  if (Array.isArray(actionValue)) {
    const selection = typeof actionValue[0] === 'string' ? actionValue[0] : '';
    const argValues = isRecord(actionValue[1]) ? toStringRecord(actionValue[1]) : {};
    const rawExpression = typeof actionValue[2] === 'string' ? actionValue[2] : undefined;
    marker.action = { selection, argValues, ...(rawExpression !== undefined ? { rawExpression } : {}) };
  }

  return marker;
}

function expandCompactPathForDeploy(value: unknown): Path | null {
  if (!Array.isArray(value)) return null;
  const name = typeof value[0] === 'string' ? value[0] : '';
  if (!name) return null;

  const isVisible = value[1] !== 0;
  const wp = (Array.isArray(value[2]) ? value[2] : [])
    .map(expandCompactWaypointForDeploy)
    .filter((waypoint): waypoint is Waypoint => waypoint !== null);
  const spd = (Array.isArray(value[3]) ? value[3] : []).filter((speed): speed is number => typeof speed === 'number' && Number.isFinite(speed));
  const mrk = (Array.isArray(value[4]) ? value[4] : [])
    .map((marker, index) => expandCompactMarkerForDeploy(marker, name, index))
    .filter((marker): marker is PathMarker => marker !== null);

  // migratePath normalizes numeric fields, syncs spd length, and assigns waypoint ids.
  return migratePath({
    name,
    id: name,
    isVisible,
    wp,
    spd,
    ...(mrk.length ? { mrk } : {}),
  });
}

function parseCompactPathsJson(json: string): ParsedPathsDeployMetadata | null {
  try {
    const parsed: unknown = JSON.parse(json);
    if (!isRecord(parsed) || parsed.v !== COMPACT_PATHS_VERSION || !Array.isArray(parsed.p)) {
      return null;
    }

    const paths = parsed.p.map(expandCompactPathForDeploy).filter((path): path is Path => path !== null);
    const result: ParsedPathsDeployMetadata = { paths };

    if (typeof parsed.sel === 'number' && parsed.sel >= 0 && parsed.sel < paths.length) {
      result.selectedPathId = paths[parsed.sel].id;
    }

    if (Array.isArray(parsed.fld)) {
      result.selectedField = normalizeFieldSelection({
        family: parsed.fld[0],
        variant: parsed.fld[1],
        accessoriesEnabled: parsed.fld[2] !== 0,
      });
    }

    if (parsed.pm === 0 || parsed.pm === 1) {
      result.pathPreviewMode = parsed.pm === 1 ? PATH_PREVIEW_MODES.SKID : PATH_PREVIEW_MODES.SWERVE;
    }

    if (typeof parsed.rot === 'number' && Number.isFinite(parsed.rot)) {
      result.pathBuilderFieldRotation = parsed.rot;
    }

    return result;
  } catch {
    return null;
  }
}

function parsePathsFromDeployLines(pathsSectionLines: string[]): ParsedPathsDeployMetadata | null {
  if (pathsSectionLines.length < 2 || !isBpsPathsDelimiterLine(pathsSectionLines[0])) {
    return null;
  }

  const encoding = readBpsCommentPayloadLine(pathsSectionLines[1]).trim();
  if (encoding !== BPS_PATHS_ENCODING_COMPACT_JSON) {
    return null;
  }

  const payload = pathsSectionLines.slice(2).map(readBpsCommentPayloadLine).join('');
  if (!payload) return null;

  return parseCompactPathsJson(payload);
}

function isBpsDeployFooterDelimiterLine(line: string): boolean {
  return line.trim() === BPS_PROJECT_FOOTER_COMMENT_PREFIX;
}

/** TODO(pre-release): Remove with legacy marker-based deploy metadata. */
function isLegacyBpsDeployMetadataLine(line: string): boolean {
  return (
    line.startsWith(BPS_PROJECT_ID_COMMENT_PREFIX) ||
    line.startsWith(BPS_PROJECT_NAME_COMMENT_PREFIX) ||
    line.startsWith(BPS_PROJECT_LAST_MODIFIED_UTC_MS_COMMENT_PREFIX) ||
    line.startsWith(BPS_PROJECT_VERSION_COMMENT_PREFIX) ||
    line.startsWith(BPS_FRAMEWORK_VERSION_COMMENT_PREFIX)
  );
}

function isBpsDeployCompactMetadataCommentLine(line: string): boolean {
  const trimmed = line.trim();
  return trimmed.startsWith('#') && !isBpsDeployFooterDelimiterLine(line);
}

function isBpsDeployMetadataLine(line: string): boolean {
  return isLegacyBpsDeployMetadataLine(line) || isBpsDeployCompactMetadataCommentLine(line);
}

/**
 * BPS deploy footer at EOF: delimiter `# __BPS_PROJECT_FOOTER__` then compact or legacy metadata comment lines.
 * Scans upward from EOF through metadata until the footer delimiter (or a non-footer line).
 */
function findTrailingBpsDeployFooterLineRange(lines: string[]): { start: number; end: number } | null {
  let end = lines.length;
  while (end > 0 && lines[end - 1] === '') {
    end -= 1;
  }
  if (end === 0) return null;

  let scan = end;
  while (scan > 0) {
    const line = lines[scan - 1];
    if (isBpsDeployFooterDelimiterLine(line)) {
      return { start: scan - 1, end };
    }
    if (isBpsDeployMetadataLine(line)) {
      scan -= 1;
      continue;
    }
    break;
  }

  return null;
}

/**
 * Legacy deploy header at the start of the file (programs deployed before metadata moved to EOF).
 * TODO(pre-release): Remove leading-header scan; metadata never shipped with leading headers.
 */
function findLeadingBpsDeployFooterLineRange(lines: string[]): { start: number; end: number } | null {
  let start = 0;
  while (start < lines.length && lines[start] === '') {
    start += 1;
  }

  let end = start;
  let footerLineCount = 0;
  while (end < lines.length && footerLineCount < MAX_BPS_LEGACY_DEPLOY_FOOTER_LINES && isLegacyBpsDeployMetadataLine(lines[end])) {
    end += 1;
    footerLineCount += 1;
  }

  return footerLineCount > 0 ? { start, end } : null;
}

/** TODO(pre-release): Remove with legacy marker-based deploy metadata. */
function applyLegacyBpsDeployMetadataLine(metadata: BpsProjectDeployMetadata, line: string): void {
  const trimmed = line.trim();
  if (!trimmed.startsWith('#')) return;

  const rest = trimmed.slice(1).trim();
  if (rest.startsWith(BPS_PROJECT_ID_MARKER)) {
    const projectId = rest.slice(BPS_PROJECT_ID_MARKER.length).trim();
    if (UUID_REGEX.test(projectId)) {
      metadata.projectId = projectId;
    }
    return;
  }

  if (rest.startsWith(BPS_PROJECT_NAME_MARKER)) {
    const projectName = normalizeProjectName(rest.slice(BPS_PROJECT_NAME_MARKER.length));
    if (projectName) {
      metadata.projectName = projectName;
    }
    return;
  }

  if (rest.startsWith(BPS_PROJECT_LAST_MODIFIED_UTC_MS_MARKER)) {
    const projectLastModifiedUtcMs = normalizeProjectLastModifiedUtcMs(rest.slice(BPS_PROJECT_LAST_MODIFIED_UTC_MS_MARKER.length));
    if (projectLastModifiedUtcMs !== undefined) {
      metadata.projectLastModifiedUtcMs = projectLastModifiedUtcMs;
    }
    return;
  }

  if (rest.startsWith(BPS_PROJECT_VERSION_MARKER)) {
    const projectVersion = normalizeVersionString(rest.slice(BPS_PROJECT_VERSION_MARKER.length));
    if (projectVersion) {
      metadata.projectVersion = projectVersion;
    }
    return;
  }

  if (rest.startsWith(BPS_FRAMEWORK_VERSION_MARKER)) {
    const frameworkVersion = normalizeStoredFrameworkVersion(rest.slice(BPS_FRAMEWORK_VERSION_MARKER.length));
    metadata.frameworkVersion = frameworkVersion;
  }
}

function parseFixedOrderCompactBpsDeployMetadataLines(values: string[]): BpsProjectDeployMetadata {
  const metadata: BpsProjectDeployMetadata = {};

  const frameworkVersion = normalizeVersionString(values[0]);
  if (frameworkVersion) {
    metadata.frameworkVersion = normalizeStoredFrameworkVersion(frameworkVersion);
  }

  const projectVersion = normalizeVersionString(values[1]);
  if (projectVersion) {
    metadata.projectVersion = projectVersion;
  }

  const projectId = values[2]?.trim();
  if (projectId && UUID_REGEX.test(projectId)) {
    metadata.projectId = projectId;
  }

  const projectName = normalizeProjectName(values[3]);
  if (projectName) {
    metadata.projectName = projectName;
  }

  const projectLastModifiedUtcMs = normalizeProjectLastModifiedUtcMs(values[4]);
  if (projectLastModifiedUtcMs !== undefined) {
    metadata.projectLastModifiedUtcMs = projectLastModifiedUtcMs;
  }

  return metadata;
}

/** TODO(pre-release): Remove with legacy variable-length compact footers (pre fixed-order 5-line format). */
function parseLegacyCompactBpsDeployMetadataLines(values: string[]): BpsProjectDeployMetadata {
  const compactValues = values.filter((value) => value.length > 0);

  const metadata: BpsProjectDeployMetadata = {};
  let index = 0;

  if (index < compactValues.length) {
    const frameworkVersion = normalizeVersionString(compactValues[index]);
    if (frameworkVersion) {
      metadata.frameworkVersion = normalizeStoredFrameworkVersion(frameworkVersion);
      index += 1;
    }
  }

  if (index < compactValues.length) {
    const projectVersion = normalizeVersionString(compactValues[index]);
    if (projectVersion) {
      metadata.projectVersion = projectVersion;
      index += 1;
    }
  }

  if (index < compactValues.length && UUID_REGEX.test(compactValues[index])) {
    metadata.projectId = compactValues[index];
    index += 1;
  }

  if (index < compactValues.length) {
    const projectName = normalizeProjectName(compactValues[index]);
    if (projectName) {
      metadata.projectName = projectName;
      index += 1;
    }
  }

  if (index < compactValues.length) {
    const projectLastModifiedUtcMs = normalizeProjectLastModifiedUtcMs(compactValues[index]);
    if (projectLastModifiedUtcMs !== undefined) {
      metadata.projectLastModifiedUtcMs = projectLastModifiedUtcMs;
    }
  }

  return metadata;
}

function parseCompactBpsDeployMetadataLines(lines: string[]): BpsProjectDeployMetadata {
  const values = lines.filter((line) => !isBpsDeployFooterDelimiterLine(line)).map((line) => line.trim().slice(1).trim());
  const hasFixedOrderFooter =
    values.length >= BPS_COMPACT_DEPLOY_FOOTER_METADATA_LINE_COUNT && normalizeVersionString(values[0]) && normalizeVersionString(values[1]);

  if (hasFixedOrderFooter) {
    return parseFixedOrderCompactBpsDeployMetadataLines(values);
  }

  // TODO(pre-release): Drop fallback; all shipped footers use fixed-order 5-line compact values.
  return parseLegacyCompactBpsDeployMetadataLines(values);
}

function parseBpsDeployMetadataLines(metadataLines: string[]): BpsProjectDeployMetadata {
  const contentLines = metadataLines.filter((line) => !isBpsDeployFooterDelimiterLine(line));
  // TODO(pre-release): Remove marker-line branch; only fixed-order compact footers will remain.
  if (contentLines.some(isLegacyBpsDeployMetadataLine)) {
    const metadata: BpsProjectDeployMetadata = {};
    for (const line of contentLines) {
      applyLegacyBpsDeployMetadataLine(metadata, line);
    }
    return metadata;
  }

  return parseCompactBpsDeployMetadataLines(contentLines);
}

/**
 * Extracts BPS deploy metadata from Python read back from a device slot.
 *
 * Reads one delimited footer section at EOF (current deploy format) and, when present, one legacy leading metadata section at the file start.
 * TODO(pre-release): Stop scanning leading legacy headers once {@link findLeadingBpsDeployFooterLineRange} is removed.
 */
export function parseBpsProjectMetadataFromPythonSource(source: string): BpsProjectDeployMetadata {
  if (!source || typeof source !== 'string') return {};

  const lines = source.split(/\r?\n/);
  const leadingRange = findLeadingBpsDeployFooterLineRange(lines);
  const trailingRange = findTrailingBpsDeployFooterLineRange(lines);

  const metadata: BpsProjectDeployMetadata = {};

  if (leadingRange) {
    const { projectSectionLines } = splitDeployFooterLines(lines.slice(leadingRange.start, leadingRange.end));
    Object.assign(metadata, parseBpsDeployMetadataLines(projectSectionLines));
  }
  if (trailingRange && (!leadingRange || trailingRange.start >= leadingRange.end)) {
    const footerLines = lines.slice(trailingRange.start, trailingRange.end);
    const { projectSectionLines, dashboardSectionLines, pathsSectionLines } = splitDeployFooterLines(footerLines);
    Object.assign(metadata, parseBpsDeployMetadataLines(projectSectionLines));
    if (dashboardSectionLines) {
      const dashboardLayout = parseDashboardLayoutFromDeployLines(dashboardSectionLines);
      if (dashboardLayout.length > 0) {
        metadata.dashboardLayout = dashboardLayout;
      }
    }
    if (pathsSectionLines) {
      const parsedPaths = parsePathsFromDeployLines(pathsSectionLines);
      if (parsedPaths) {
        metadata.paths = parsedPaths.paths;
        if (parsedPaths.selectedPathId !== undefined) metadata.selectedPathId = parsedPaths.selectedPathId;
        if (parsedPaths.selectedField !== undefined) metadata.selectedField = parsedPaths.selectedField;
        if (parsedPaths.pathPreviewMode !== undefined) metadata.pathPreviewMode = parsedPaths.pathPreviewMode;
        if (parsedPaths.pathBuilderFieldRotation !== undefined) metadata.pathBuilderFieldRotation = parsedPaths.pathBuilderFieldRotation;
      }
    }
  }

  return metadata;
}

/**
 * Parses BPS project id from deploy metadata comments in Python source.
 */
export function parseBpsProjectIdFromPythonSource(source: string): string | null {
  return parseBpsProjectMetadataFromPythonSource(source).projectId ?? null;
}

/**
 * Removes BPS deploy footer/header comments from Python read back from a device slot.
 *
 * Strips at most one delimited footer section from EOF and one legacy header section from the file start.
 * TODO(pre-release): Stop stripping leading legacy headers once {@link findLeadingBpsDeployFooterLineRange} is removed.
 */
export function stripBpsProjectMetadataFromDeployedPython(source: string): string {
  const lines = source.split(/\r?\n/);
  const leadingRange = findLeadingBpsDeployFooterLineRange(lines);
  const trailingRange = findTrailingBpsDeployFooterLineRange(lines);

  let bodyStart = 0;
  let bodyEnd = lines.length;

  if (leadingRange) {
    bodyStart = leadingRange.end;
  }

  if (trailingRange && trailingRange.start >= bodyStart) {
    bodyEnd = trailingRange.start;
  }

  const bodyLines = lines.slice(bodyStart, bodyEnd);
  while (bodyLines.length > 0 && bodyLines[bodyLines.length - 1] === '') {
    bodyLines.pop();
  }

  return bodyLines.length === 0 ? '' : `${bodyLines.join('\n')}\n`;
}

/**
 * Backward-compatible alias for stripping deploy-only metadata from Python read back from a device slot.
 */
export function stripBpsProjectIdFromDeployedPython(source: string): string {
  return stripBpsProjectMetadataFromDeployedPython(source);
}

export interface SaveSlotLike {
  isEmpty?: boolean;
  programTitle?: string;
  bpsProjectId?: string;
  bpsProjectLastModifiedUtcMs?: number;
  deviceFilename?: string;
}

export function getPositiveSaveSlotNumber(slot: unknown): number | null {
  const slotNumber = typeof slot === 'string' ? Number(slot.trim()) : Number(slot);
  return Number.isInteger(slotNumber) && slotNumber > 0 ? slotNumber : null;
}

export function getValidSaveSlotNumber(slot: unknown, maxSlot = 8): number | null {
  const slotNumber = getPositiveSaveSlotNumber(slot);
  return slotNumber !== null && slotNumber <= maxSlot ? slotNumber : null;
}

function isEmptyPlaceholderName(name: string | undefined): boolean {
  if (!name) return false;
  const normalized = String(name).trim().split('/').pop()?.split('\\').pop()?.toLowerCase() ?? '';
  return normalized === 'empty.py';
}

/**
 * Human-friendly label for slot program names.
 * Keeps internal metadata unchanged, but hides a trailing ".py" in UI labels.
 */
export function getSaveSlotDisplayProgramName(programTitle: string | undefined): string {
  if (!programTitle) return '';
  const trimmed = String(programTitle).trim();
  if (!trimmed) return '';
  return trimmed.replace(/\.py$/i, '');
}

/**
 * Returns true when slot metadata indicates no deployed program content.
 */
export function isSaveSlotEmpty(slot: SaveSlotLike | null | undefined): boolean {
  if (!slot) return true;
  if (slot.isEmpty) return true;
  if (isEmptyPlaceholderName(slot.programTitle) || isEmptyPlaceholderName(slot.deviceFilename)) return true;
  const hasProgram = !!(slot.programTitle && String(slot.programTitle).trim()) || !!slot.deviceFilename;
  return !hasProgram;
}

/** Derives a filesystem-safe Python filename used when naming Swerve `.bps` backup downloads. */
export function projectNameToDeviceFilename(projectName: string): string {
  const baseName = projectName.replace(/[^A-Za-z0-9_-]/g, '_') || 'program';
  return baseName.endsWith('.py') ? baseName : `${baseName}.py`;
}

/**
 * Returns true when slot metadata proves it contains the same deployed revision
 * as the loaded project. Falls back to id-only matching when the loaded project
 * does not have a usable lastModified timestamp.
 */
export function saveSlotMatchesProject(
  slot: SaveSlotLike | null | undefined,
  loadedProjectId: string | null | undefined,
  loadedProjectLastModifiedUtcMs?: number
): boolean {
  if (!slot || !loadedProjectId || slot.bpsProjectId !== loadedProjectId) {
    return false;
  }

  if (loadedProjectLastModifiedUtcMs === undefined) {
    return true;
  }

  return slot.bpsProjectLastModifiedUtcMs === loadedProjectLastModifiedUtcMs;
}

/**
 * Chooses the best save slot for a project.
 * Priority:
 * 1) exact project revision match
 * 2) same project id (when no exact revision match exists)
 * 3) first empty slot
 * 4) slot 1
 */
export function getPreferredSaveSlot(
  slotStorage: Record<string, SaveSlotLike | null | undefined> | null | undefined,
  loadedProjectId?: string | null,
  loadedProjectLastModifiedUtcMs?: number
): number {
  if (!slotStorage) return 1;
  const entries = Object.entries(slotStorage).filter((e): e is [string, SaveSlotLike] => Boolean(e[1]));
  if (entries.length === 0) return 1;

  if (loadedProjectId) {
    const ownRevisionEntry = entries.find(([, s]) => saveSlotMatchesProject(s, loadedProjectId, loadedProjectLastModifiedUtcMs));
    if (ownRevisionEntry) return slotNumberFromSlotName(ownRevisionEntry[0]);
    const ownProjectEntry = entries.find(([, s]) => s?.bpsProjectId === loadedProjectId);
    if (ownProjectEntry) return slotNumberFromSlotName(ownProjectEntry[0]);
  }

  const emptyEntry = entries.find(([, s]) => isSaveSlotEmpty(s));
  if (emptyEntry) return slotNumberFromSlotName(emptyEntry[0]);

  return 1;
}

/**
 * Returns true if selecting this occupied slot may overwrite another project's program and needs confirmation.
 */
export function saveSlotNeedsReplaceConfirmation(
  slot: SaveSlotLike | null | undefined,
  loadedProjectId: string | null | undefined,
  loadedProjectLastModifiedUtcMs?: number
): boolean {
  if (isSaveSlotEmpty(slot)) return false;
  if (!slot) return false;

  if (!loadedProjectId) return true;

  if (saveSlotMatchesProject(slot, loadedProjectId, loadedProjectLastModifiedUtcMs)) return false;

  return true;
}

export interface BpsDeployMetadataResult {
  /** Python source with the deploy footer appended. */
  python: string;
  /** True when path metadata was dropped because it exceeded {@link BPS_PATHS_DEPLOY_MAX_BYTES}. */
  pathsOmitted: boolean;
}

/**
 * Appends the BPS deploy footer and reports whether path metadata was omitted for size.
 * Prefer {@link appendBpsProjectMetadataToDeployedPython} when the report is not needed.
 */
export function appendBpsProjectMetadataToDeployedPythonWithReport(
  pythonScript: string,
  {
    projectId,
    projectName,
    projectLastModifiedUtcMs,
    projectVersion,
    frameworkVersion,
    dashboardLayout,
    paths,
    selectedPathId,
    selectedField,
    pathPreviewMode,
    pathBuilderFieldRotation,
  }: BpsProjectDeployMetadata
): BpsDeployMetadataResult {
  const normalizedProjectId = typeof projectId === 'string' && UUID_REGEX.test(projectId.trim()) ? projectId.trim() : undefined;
  const normalizedProjectName = normalizeProjectName(projectName);
  const normalizedProjectLastModifiedUtcMs = normalizeProjectLastModifiedUtcMs(projectLastModifiedUtcMs);
  const normalizedProjectVersion = normalizeVersionString(projectVersion);
  const dashboardDeployLines = serializeDashboardLayoutForDeploy(dashboardLayout);
  let { lines: pathsDeployLines, omitted: pathsOmitted } = serializePathsForDeploy(paths, {
    selectedPathId,
    selectedField,
    pathPreviewMode,
    pathBuilderFieldRotation,
  });
  const shouldAppendFooter =
    normalizedProjectId !== undefined ||
    normalizedProjectName !== undefined ||
    normalizedProjectLastModifiedUtcMs !== undefined ||
    normalizedProjectVersion !== undefined ||
    dashboardDeployLines.length > 0 ||
    pathsDeployLines.length > 0;

  if (!shouldAppendFooter) return { python: pythonScript, pathsOmitted };

  const normalizedFrameworkVersion = resolveDeployFrameworkVersionString(frameworkVersion);
  const resolvedProjectVersion = normalizedProjectVersion ?? projectVersionToString(CURRENT_PROJECT_VERSION);
  const metadataLines = [
    formatBpsDeployMetadataValueCommentLine(normalizedFrameworkVersion),
    formatBpsDeployMetadataValueCommentLine(resolvedProjectVersion),
    formatBpsDeployMetadataValueCommentLine(normalizedProjectId ?? ''),
    formatBpsDeployMetadataValueCommentLine(normalizedProjectName ?? ''),
    formatBpsDeployMetadataValueCommentLine(normalizedProjectLastModifiedUtcMs ?? ''),
  ];

  const normalizedPythonScript = pythonScript.endsWith('\n') ? pythonScript : `${pythonScript}\n`;

  // Enforce a single combined-footer budget. Per-section caps can each pass yet still emit an oversized footer
  // once dashboard + paths (plus chunk-comment overhead and base metadata) are present together. Paths are
  // best-effort, so drop them first and report it; if base metadata + dashboard alone still overflow, fail the
  // deploy (mirrors serializeDashboardLayoutForDeploy's hard cap) rather than silently shipping oversized bytes.
  const buildFooter = (includePaths: boolean): string =>
    [BPS_PROJECT_FOOTER_COMMENT_PREFIX, ...metadataLines, ...dashboardDeployLines, ...(includePaths ? pathsDeployLines : [])].join('\n');

  // Measure the footer exactly as it ships — including the trailing newline appended in the returned string —
  // so a footer sitting right at the cap cannot pass the check and then send one extra byte to the device.
  const shippedFooterByteLength = (candidate: string): number => getUtf8ByteLength(`${candidate}\n`);

  let footer = buildFooter(true);
  if (shippedFooterByteLength(footer) > BPS_DEPLOY_FOOTER_MAX_BYTES && pathsDeployLines.length > 0) {
    pathsDeployLines = [];
    pathsOmitted = true;
    footer = buildFooter(false);
  }

  const footerByteLength = shippedFooterByteLength(footer);
  if (footerByteLength > BPS_DEPLOY_FOOTER_MAX_BYTES) {
    throw new BpsDashboardDeployMetadataError(`Deploy footer metadata exceeds ${BPS_DEPLOY_FOOTER_MAX_BYTES} bytes (${footerByteLength} bytes)`);
  }

  return { python: `${normalizedPythonScript}${footer}\n`, pathsOmitted };
}

export function appendBpsProjectMetadataToDeployedPython(pythonScript: string, metadata: BpsProjectDeployMetadata): string {
  return appendBpsProjectMetadataToDeployedPythonWithReport(pythonScript, metadata).python;
}

/** @deprecated Use {@link appendBpsProjectMetadataToDeployedPython}; name retained for older imports. */
export function prependBpsProjectMetadataToDeployedPython(pythonScript: string, metadata: BpsProjectDeployMetadata): string {
  return appendBpsProjectMetadataToDeployedPython(pythonScript, metadata);
}

/**
 * Appends deploy-only project id metadata when name / last-modified are unavailable.
 */
export function appendBpsProjectIdToDeployedPython(pythonScript: string, projectId: string): string {
  return appendBpsProjectMetadataToDeployedPython(pythonScript, { projectId });
}

/** @deprecated Use {@link appendBpsProjectIdToDeployedPython}; name retained for older imports. */
export function prependBpsProjectIdToDeployedPython(pythonScript: string, projectId: string): string {
  return appendBpsProjectIdToDeployedPython(pythonScript, projectId);
}
